/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * Open-redirect and `javascript:`-XSS guards for URIs that arrive from user
 * input (query params, path segments) and end up in `window.location.assign`.
 *
 * Both guards resolve the candidate with the WHATWG URL parser relative to the
 * current location, so the outcome matches how a real navigation would resolve
 * the value, and both require an `http:`/`https:` scheme before looking at the
 * host. The scheme check is load-bearing: the URL parser happily reads an
 * authority out of a non-special scheme, so `javascript://www.recreation.gov/%0aalert(1)`
 * parses with `hostname === 'www.recreation.gov'` and would pass a hostname
 * comparison on its own while still executing as script. A URI that can not be
 * parsed at all is treated as unsafe.
 */

/** Schemes a redirect target may use. Everything else is non-navigational. */
const NAVIGABLE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Resolve `uri` against the current location, returning it only when it is a
 * real http(s) navigation target.
 */
function resolveNavigable(uri: string | null | undefined): URL | null {
    if (!uri) {
        return null;
    }

    try {
        const resolved = new URL(uri, window.location.href);
        return NAVIGABLE_PROTOCOLS.has(resolved.protocol) ? resolved : null;
    } catch {
        return null;
    }
}

/** The hostname of a configured app root (`SITE_ROOT`, `HUB_ROOT`, …). */
function hostnameOf(root: string | undefined): string | null {
    if (!root) {
        return null;
    }

    try {
        return new URL(root).hostname;
    } catch {
        return null;
    }
}

/**
 * Returns whether `uri` targets the same host as the current page.
 *
 * Relative paths and same-origin absolute URLs are allowed, while cross-origin,
 * protocol-relative (`//evil.example.com`), and non-navigational (`javascript:`,
 * `data:`) URIs are rejected. Use this for a redirect that should never leave
 * the app it started in — the mobile handoff's `next` parameter, for example.
 *
 * @param uri - The candidate target URI (may be null when the param is absent).
 * @returns `true` only when the resolved hostname matches the current hostname.
 */
export function isSameOriginUri(uri: string | null | undefined): boolean {
    const resolved = resolveNavigable(uri);

    return resolved !== null && resolved.hostname === window.location.hostname;
}

/**
 * Returns whether `uri` targets one of our own apps: the current host, the
 * public site (`SITE_ROOT`) or the internal Hub (`HUB_ROOT`).
 *
 * This is {@link isSameOriginUri} widened to the handful of first-party hosts a
 * flow may legitimately cross. The CSR login hop needs that: the target is
 * chosen on `manage.recreation.gov` and consumed on `www.recreation.gov`, so a
 * strict same-origin check would reject a valid Hub destination. Every host
 * outside the configured roots — and every non-http(s) scheme — is still
 * rejected, which is what closes the open redirect and the `javascript:` sink.
 *
 * Roots come from config, so this tracks the environment (`recreation.gov` in
 * prod, `r1s-dev.com` in dev, `localhost:<port>` locally) without a hardcoded
 * domain list. An unset or unparseable root simply contributes no host.
 *
 * @param uri - The candidate target URI (may be null when the param is absent).
 * @returns `true` only when the resolved hostname is one of ours.
 */
export function isFirstPartyUri(uri: string | null | undefined): boolean {
    const resolved = resolveNavigable(uri);

    if (resolved === null) {
        return false;
    }

    return (
        resolved.hostname === window.location.hostname ||
        resolved.hostname === hostnameOf(process.env.SITE_ROOT) ||
        resolved.hostname === hostnameOf(process.env.HUB_ROOT)
    );
}
